/**
 * Admin auth (login / register / 2FA). 라우트 admin.php auth 그룹과 동기화.
 * data-controller="AdminAuthController"
 * data-action="login"|"handleLogin"|"showRegisterForm"|"register"|"logout"
 *           |"showTwoFactorForm"|"verifyTwoFactor"
 *           |"showTwoFactorSetup"|"confirmTwoFactorSetup"
 */

import { Form } from "../../core/Form";
import { Logger } from "../../utils/Logger";
import { Modal } from "../../utils/Modal";
import { Toast } from "../../utils/Toast";

const FORM_SELECTOR = ".admin-auth__form";
const LOADER_SELECTOR = ".admin-auth__loader";
const SAVED_EMAIL_KEY = "admin_auth_saved_email";

function getMessages(): Record<string, string> {
  const lang = document.documentElement.lang || "ko";
  const isKo = lang.startsWith("ko");
  return {
    emailRequired: isKo ? "이메일을 입력해주세요." : "Please enter your email.",
    passwordRequired: isKo ? "비밀번호를 입력해주세요." : "Please enter your password.",
    nameRequired: isKo ? "이름을 입력해주세요." : "Please enter your name.",
    passwordConfirmRequired: isKo ? "비밀번호 확인을 입력해주세요." : "Please confirm your password.",
    passwordMismatch: isKo ? "비밀번호가 일치하지 않습니다." : "Passwords do not match.",
    requestFailed: isKo ? "요청에 실패했습니다." : "Request failed.",
    errorOccurred: isKo ? "오류가 발생했습니다." : "An error occurred.",
  };
}

function validateLogin(form: HTMLFormElement): string | null {
  const messages = getMessages();
  const email = (form.querySelector<HTMLInputElement>('[name="email"]')?.value ?? "").trim();
  const password = (form.querySelector<HTMLInputElement>('[name="password"]')?.value ?? "").trim();
  if (!email) return messages.emailRequired;
  if (!password) return messages.passwordRequired;
  return null;
}

function validateRegister(form: HTMLFormElement): string | null {
  const messages = getMessages();
  const name = (form.querySelector<HTMLInputElement>('[name="name"]')?.value ?? "").trim();
  const email = (form.querySelector<HTMLInputElement>('[name="email"]')?.value ?? "").trim();
  const password = (form.querySelector<HTMLInputElement>('[name="password"]')?.value ?? "").trim();
  const confirm = (form.querySelector<HTMLInputElement>('[name="password_confirmation"]')?.value ?? "").trim();
  if (!name) return messages.nameRequired;
  if (!email) return messages.emailRequired;
  if (!password) return messages.passwordRequired;
  if (!confirm) return messages.passwordConfirmRequired;
  if (password !== confirm) return messages.passwordMismatch;
  return null;
}

function getEmailInput(form: HTMLFormElement): HTMLInputElement | null {
  return form.querySelector<HTMLInputElement>('[name="email"]');
}

function getSaveIdCheckbox(form: HTMLFormElement): HTMLInputElement | null {
  return form.querySelector<HTMLInputElement>("[data-save-id]");
}

function hydrateSavedEmail(form: HTMLFormElement): void {
  try {
    const savedEmail = localStorage.getItem(SAVED_EMAIL_KEY);
    const emailInput = getEmailInput(form);
    const saveIdCheckbox = getSaveIdCheckbox(form);
    if (!savedEmail || !emailInput || !saveIdCheckbox) return;

    emailInput.value = savedEmail;
    saveIdCheckbox.checked = true;
  } catch {
    // localStorage unavailable
  }
}

function syncSavedEmail(form: HTMLFormElement): void {
  try {
    const emailInput = getEmailInput(form);
    const saveIdCheckbox = getSaveIdCheckbox(form);
    const email = emailInput?.value.trim() ?? "";

    if (saveIdCheckbox?.checked && email !== "") {
      localStorage.setItem(SAVED_EMAIL_KEY, email);
      return;
    }

    localStorage.removeItem(SAVED_EMAIL_KEY);
  } catch {
    // localStorage unavailable
  }
}

function handleAuthSuccess(data: Record<string, unknown>): void {
  const msg = (data?.msg ?? data?.message ?? "") as string;
  const redirectUrl = (data?.redirect ?? data?.redirect_url) as string | undefined;
  if (redirectUrl) {
    window.location.href = redirectUrl;
    return;
  }
  if (msg) void Modal.alert(msg);
}

function initPasswordToggles(root: Document | HTMLElement = document): void {
  root.querySelectorAll<HTMLButtonElement>(".admin-auth__password-toggle").forEach((btn) => {
    btn.addEventListener("click", () => {
      const wrap = btn.closest(".admin-auth__password-wrap");
      const input = wrap?.querySelector<HTMLInputElement>("input");
      const icon = btn.querySelector<HTMLElement>("i");
      if (!input || !icon) return;

      const isPassword = input.type === "password";
      input.type = isPassword ? "text" : "password";
      icon.classList.toggle("fa-eye", !isPassword);
      icon.classList.toggle("fa-eye-slash", isPassword);
      btn.setAttribute("aria-label", isPassword ? "비밀번호 숨기기" : "비밀번호 보기");
    });
  });
}

// ──────────────────────────────────────────────────────────────
// 2FA 공통 헬퍼
// ──────────────────────────────────────────────────────────────

function validateOtpCode(form: HTMLFormElement): string | null {
  const lang = document.documentElement.lang || "ko";
  const isKo = lang.startsWith("ko");
  const code = (form.querySelector<HTMLInputElement>('[name="code"]')?.value ?? "").trim();
  if (!code) return isKo ? "인증 코드를 입력해주세요." : "Please enter the verification code.";
  if (!/^\d{6}$/.test(code)) return isKo ? "인증 코드는 6자리 숫자입니다." : "The code must be 6 digits.";
  return null;
}

function autoSubmitOnComplete(form: HTMLFormElement): void {
  const codeInput = form.querySelector<HTMLInputElement>('[name="code"]');
  if (!codeInput) return;
  codeInput.addEventListener("input", () => {
    if (/^\d{6}$/.test(codeInput.value)) {
      form.requestSubmit?.();
    }
  });
  codeInput.focus();
}

/**
 * OTP 발송 버튼 공통 설정.
 * @param extraBody  발송 시 함께 보낼 추가 필드
 * @param onSuccess  성공/totp_qr 응답 콜백
 */
function setupSendButton(
  btn: HTMLButtonElement,
  url: string,
  extraBody?: () => Record<string, string>,
  onSuccess?: (data: Record<string, unknown>) => void,
): void {
  const lang = document.documentElement.lang || "ko";
  const isKo = lang.startsWith("ko");
  const originalHTML = btn.innerHTML;
  let countdownTimer: ReturnType<typeof setInterval> | null = null;

  function startCountdown(seconds: number): void {
    btn.disabled = true;
    if (countdownTimer) clearInterval(countdownTimer);
    let remaining = seconds;
    const tick = (): void => {
      btn.textContent = isKo ? `${remaining}초 후 재발송` : `Resend in ${remaining}s`;
      remaining--;
      if (remaining < 0) {
        clearInterval(countdownTimer!);
        countdownTimer = null;
        btn.disabled = false;
        btn.innerHTML = originalHTML;
      }
    };
    tick();
    countdownTimer = setInterval(tick, 1000);
  }

  btn.addEventListener("click", async () => {
    if (btn.disabled) return;
    btn.disabled = true;
    btn.textContent = isKo ? "처리 중…" : "Processing…";

    const csrfMeta = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]');
    const csrfToken = csrfMeta?.content ?? "";
    const body: Record<string, string> = { _token: csrfToken, ...(extraBody?.() ?? {}) };

    try {
      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-Requested-With": "XMLHttpRequest",
          "X-CSRF-TOKEN": csrfToken,
        },
        body: JSON.stringify(body),
      });

      const data = (await res.json()) as {
        result?: string;
        msg?: string;
        cooldown?: number;
        secret?: string;
        qr_url?: string;
      };

      if (data.result === "ok" || data.result === "success") {
        Toast.success(data.msg ?? (isKo ? "인증번호가 발송되었습니다." : "Code sent."));
        startCountdown(60);
        onSuccess?.(data as Record<string, unknown>);
      } else if (data.result === "totp_qr") {
        btn.disabled = false;
        btn.innerHTML = originalHTML;
        onSuccess?.(data as Record<string, unknown>);
      } else {
        const cooldown = Number(data.cooldown ?? 0);
        if (cooldown > 0) {
          Toast.error(data.msg ?? (isKo ? `${cooldown}초 후 다시 시도해주세요.` : `Please retry in ${cooldown}s.`));
          startCountdown(cooldown);
        } else {
          Toast.error(data.msg ?? (isKo ? "발송에 실패했습니다." : "Failed to send code."));
          btn.disabled = false;
          btn.innerHTML = originalHTML;
        }
      }
    } catch {
      Toast.error(isKo ? "네트워크 오류가 발생했습니다." : "A network error occurred.");
      btn.disabled = false;
      btn.innerHTML = originalHTML;
    }
  });
}

// ──────────────────────────────────────────────────────────────
// 타임라인 스텝 제어
// ──────────────────────────────────────────────────────────────

function markStepDone(stepNum: number): void {
  const step = document.querySelector<HTMLElement>(`[data-step="${stepNum}"]`);
  if (!step) return;
  step.classList.remove("admin-auth__step--active", "admin-auth__step--pending");
  step.classList.add("admin-auth__step--done");

  const numEl = step.querySelector<HTMLElement>(`[data-step-dot="${stepNum}"] .admin-auth__step-num`);
  if (numEl) numEl.innerHTML = '<i class="fa-solid fa-check" aria-hidden="true"></i>';

  const line = step.querySelector<HTMLElement>(".admin-auth__step-line");
  if (line) line.classList.add("admin-auth__step-line--done");
}

function activateStep(stepNum: number): void {
  const step = document.querySelector<HTMLElement>(`[data-step="${stepNum}"]`);
  if (!step) return;
  step.classList.remove("admin-auth__step--pending", "admin-auth__step--done");
  step.classList.add("admin-auth__step--active");

  const body = step.querySelector<HTMLElement>(`[data-step-body="${stepNum}"]`);
  if (body) body.hidden = false;
}

async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    const el = document.createElement("textarea");
    el.value = text;
    el.style.cssText = "position:fixed;opacity:0;top:0;left:0";
    document.body.appendChild(el);
    el.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(el);
    return ok;
  }
}

// ──────────────────────────────────────────────────────────────
// 소셜 로그인 팝업
// ──────────────────────────────────────────────────────────────

const SOCIAL_POPUP_WIDTH  = 500;
const SOCIAL_POPUP_HEIGHT = 600;
const SOCIAL_POPUP_NAME   = "social_auth_popup";

function openSocialPopup(url: string): Window | null {
  const left = Math.round(window.screenX + (window.outerWidth  - SOCIAL_POPUP_WIDTH)  / 2);
  const top  = Math.round(window.screenY + (window.outerHeight - SOCIAL_POPUP_HEIGHT) / 2);
  const features = [
    `width=${SOCIAL_POPUP_WIDTH}`,
    `height=${SOCIAL_POPUP_HEIGHT}`,
    `left=${left}`,
    `top=${top}`,
    "scrollbars=yes",
    "resizable=no",
    "status=no",
    "toolbar=no",
    "menubar=no",
    "location=no",
  ].join(",");
  return window.open(url, SOCIAL_POPUP_NAME, features);
}

function initSocialButtons(): void {
  const buttons = document.querySelectorAll<HTMLButtonElement>("[data-social-provider]");
  if (buttons.length === 0) return;

  buttons.forEach((btn) => {
    btn.addEventListener("click", () => {
      const fallbackUrl = btn.getAttribute("data-fallback-url") ?? "";
      if (!fallbackUrl) return;

      const popupUrl = fallbackUrl + (fallbackUrl.includes("?") ? "&" : "?") + "popup=1";
      const popup = openSocialPopup(popupUrl);

      if (!popup) {
        Logger.warn("AdminAuthController: social popup blocked, falling back to redirect");
        window.location.href = fallbackUrl;
        return;
      }

      function cleanup(): void {
        window.removeEventListener("message", onMessage);
        clearInterval(closedWatcher);
      }

      function onMessage(event: MessageEvent): void {
        if (event.origin !== window.location.origin) return;

        const data = event.data as {
          type?: string;
          status?: string;
          message?: string;
          redirect?: string;
        };
        if (data?.type !== "social-auth") return;

        cleanup();

        if (data.status === "success") {
          const msg      = (data.message ?? "로그인되었습니다.") as string;
          const redirect = (data.redirect ?? "") as string;
          if (redirect) {
            Toast.success(msg);
            setTimeout(() => { window.location.href = redirect; }, 800);
          } else {
            void Modal.alert(msg);
          }
        } else {
          Toast.error((data.message ?? "소셜 로그인 중 오류가 발생했습니다.") as string);
        }
      }

      window.addEventListener("message", onMessage);

      const closedWatcher = setInterval(() => {
        if (popup.closed) cleanup();
      }, 500);
    });
  });
}

export const AdminAuthController = {
  login(): void {
    Logger.info("AdminAuthController.login");
    const form = document.querySelector<HTMLFormElement>(FORM_SELECTOR);
    if (!form) return;

    initPasswordToggles();
    hydrateSavedEmail(form);
    form.addEventListener("submit", () => syncSavedEmail(form));

    const messages = getMessages();
    const f = new Form(form, {
      submitUrl: form.action || undefined,
      validate: validateLogin,
      loaderSelector: LOADER_SELECTOR,
      requestFailedMessage: messages.requestFailed,
      invalidResponseMessage: messages.errorOccurred,
      onSuccess: handleAuthSuccess,
      onFail: (message) => void Modal.alert(message),
    });
    f.init();
    initSocialButtons();
  },

  handleLogin(): void {
    Logger.debug("AdminAuthController.handleLogin (no-op)");
  },

  showRegisterForm(): void {
    Logger.info("AdminAuthController.showRegisterForm");
    AdminAuthController.register();
  },

  register(): void {
    Logger.info("AdminAuthController.register");
    const form = document.querySelector<HTMLFormElement>(FORM_SELECTOR);
    if (!form) return;

    initPasswordToggles();
    const messages = getMessages();
    const f = new Form(form, {
      submitUrl: form.action || undefined,
      validate: validateRegister,
      loaderSelector: LOADER_SELECTOR,
      requestFailedMessage: messages.requestFailed,
      invalidResponseMessage: messages.errorOccurred,
      onSuccess: handleAuthSuccess,
      onFail: (message) => void Modal.alert(message),
    });
    f.init();
  },

  logout(): void {
    Logger.debug("AdminAuthController.logout (no-op)");
  },

  // ──────────────────────────────────────────────────────────────
  // 2FA — Verify (매 로그인)
  // ──────────────────────────────────────────────────────────────

  showTwoFactorForm(): void {
    Logger.info("AdminAuthController.showTwoFactorForm");

    const form = document.querySelector<HTMLFormElement>(FORM_SELECTOR);
    const channelKey = form?.getAttribute("data-2fa-channel") ?? "email";
    const isTotp = channelKey === "totp";

    const sendBtn = document.querySelector<HTMLButtonElement>("[data-2fa-send]");
    const sendUrl = sendBtn?.getAttribute("data-2fa-send-url") ?? "";

    if (isTotp) {
      // TOTP: 발송 단계 없이 바로 코드 입력 활성화
      if (sendBtn) sendBtn.hidden = true;
      const codeStep = document.querySelector<HTMLElement>("[data-2fa-code-step]");
      if (codeStep) {
        codeStep.classList.remove("admin-auth__step--pending");
        codeStep.classList.add("admin-auth__step--active");
        const stepNum = codeStep.getAttribute("data-step") ?? "2";
        const body = codeStep.querySelector<HTMLElement>(`[data-step-body="${stepNum}"]`);
        if (body) body.hidden = false;
      }
    } else if (sendBtn && sendUrl) {
      // 발송 성공 → step2 done + step3 active
      setupSendButton(sendBtn, sendUrl, undefined, () => {
        markStepDone(2);
        activateStep(3);
      });
    }

    if (!form) return;

    const messages = getMessages();
    const f = new Form(form, {
      submitUrl: form.action || undefined,
      validate: validateOtpCode,
      loaderSelector: LOADER_SELECTOR,
      requestFailedMessage: messages.requestFailed,
      invalidResponseMessage: messages.errorOccurred,
      onSuccess: handleAuthSuccess,
      onFail: (message) => Toast.error(message),
    });
    f.init();
    autoSubmitOnComplete(form);
  },

  verifyTwoFactor(): void {
    Logger.debug("AdminAuthController.verifyTwoFactor (alias → showTwoFactorForm)");
    AdminAuthController.showTwoFactorForm();
  },

  // ──────────────────────────────────────────────────────────────
  // 2FA — Setup / Re-enroll
  // ──────────────────────────────────────────────────────────────

  showTwoFactorSetup(): void {
    Logger.info("AdminAuthController.showTwoFactorSetup");

    const sendUrl = document.querySelector<HTMLElement>("[data-2fa-send]")?.getAttribute("data-2fa-send-url") ?? "";
    let selectedChannel = "";

    // 채널 카드 클릭/키보드 선택
    const cards = document.querySelectorAll<HTMLElement>("[data-2fa-card]");

    function handleCardSelect(card: HTMLElement): void {
      if (card.getAttribute("aria-disabled") === "true") return;
      const key = card.getAttribute("data-2fa-card") ?? "";
      if (!key) return;

      cards.forEach((c) => c.setAttribute("aria-checked", "false"));
      card.setAttribute("aria-checked", "true");
      selectedChannel = key;

      const actionWrapper = document.querySelector<HTMLElement>("[data-step-action='1']");
      if (actionWrapper) actionWrapper.hidden = false;
    }

    cards.forEach((card) => {
      card.addEventListener("click", () => handleCardSelect(card));
      card.addEventListener("keydown", (e: KeyboardEvent) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleCardSelect(card);
        }
      });
    });

    // Step 1 → Step 2
    document.querySelector<HTMLButtonElement>("[data-2fa-step-next='1']")
      ?.addEventListener("click", () => {
        if (!selectedChannel) return;
        markStepDone(1);
        activateStep(2);
        showStep2Panel(selectedChannel);
      });

    function showStep2Panel(channelKey: string): void {
      document.querySelectorAll<HTMLElement>("[data-2fa-step2-panel]").forEach((p) => { p.hidden = true; });
      const panel = document.querySelector<HTMLElement>(`[data-2fa-step2-panel="${channelKey}"]`);
      if (panel) panel.hidden = false;

      const sendBtn = document.querySelector<HTMLButtonElement>("[data-2fa-send]");
      if (!sendBtn) return;

      const actionWrapper = sendBtn.closest<HTMLElement>("[data-step-action='2']");
      if (actionWrapper) actionWrapper.hidden = false;

      sendBtn.hidden = false;

      if (channelKey === "totp") {
        sendBtn.innerHTML = '<i class="fa-regular fa-qrcode" aria-hidden="true"></i> QR 코드 불러오기';
        setupSendButton(
          sendBtn,
          sendUrl,
          () => ({ channel: channelKey }),
          (data) => {
            const d = data as { result?: string; secret?: string; qr_url?: string };
            if (d.result === "totp_qr") {
              renderTotpQr(d.secret ?? "", d.qr_url ?? "");
              markStepDone(2);
              activateStep(3);
              initStep3Form(channelKey);
            }
          },
        );
      } else {
        sendBtn.innerHTML = '<i class="fa-regular fa-paper-plane" aria-hidden="true"></i> 인증번호 발송';
        setupSendButton(
          sendBtn,
          sendUrl,
          () => {
            const phone = document.querySelector<HTMLInputElement>("[data-2fa-phone]")?.value.trim() ?? "";
            return { channel: channelKey, phone };
          },
          () => {
            markStepDone(2);
            activateStep(3);
            initStep3Form(channelKey);
          },
        );
      }
    }

    function renderTotpQr(secret: string, qrUrl: string): void {
      const qrWrap = document.querySelector<HTMLElement>("[data-2fa-totp-qr-wrap]");
      const qrImg = document.querySelector<HTMLImageElement>("[data-2fa-totp-qr]");
      const secretText = document.querySelector<HTMLElement>("[data-2fa-totp-secret-text]");
      const secretEl = document.querySelector<HTMLElement>("[data-2fa-totp-secret]");

      if (qrImg) {
        qrImg.src = qrUrl;
        if (qrWrap) qrWrap.hidden = false;
      }
      if (secretText) secretText.textContent = secret;
      if (secretEl) {
        secretEl.addEventListener("click", async () => {
          const ok = await copyToClipboard(secret);
          Toast[ok ? "success" : "error"](ok ? "시크릿 키가 복사되었습니다." : "복사에 실패했습니다.");
        });
      }
    }

    function initStep3Form(channelKey: string): void {
      const hint = document.querySelector<HTMLElement>("[data-2fa-step3-hint]");
      if (hint) {
        const hints: Record<string, string> = {
          totp: "Google Authenticator 앱에 표시된 6자리 코드를 입력하세요.",
          sms: "입력한 휴대폰 번호로 발송된 6자리 코드를 입력하세요.",
          email: "이메일로 발송된 6자리 코드를 입력하세요.",
        };
        hint.textContent = hints[channelKey] ?? hints["email"];
      }

      const hiddenChannel = document.querySelector<HTMLInputElement>("[data-2fa-hidden-channel]");
      if (hiddenChannel) hiddenChannel.value = channelKey;

      const form = document.querySelector<HTMLFormElement>(FORM_SELECTOR);
      if (!form) return;

      const messages = getMessages();
      const f = new Form(form, {
        submitUrl: form.action || undefined,
        validate: validateOtpCode,
        loaderSelector: LOADER_SELECTOR,
        requestFailedMessage: messages.requestFailed,
        invalidResponseMessage: messages.errorOccurred,
        onSuccess: handleAuthSuccess,
        onFail: (message) => Toast.error(message),
      });
      f.init();
      autoSubmitOnComplete(form);
    }
  },

  confirmTwoFactorSetup(): void {
    Logger.debug("AdminAuthController.confirmTwoFactorSetup (alias → showTwoFactorSetup)");
    AdminAuthController.showTwoFactorSetup();
  },

  // ──────────────────────────────────────────────────────────────
  // 비밀번호 찾기 / 재설정 (비로그인 접근 가능)
  // ──────────────────────────────────────────────────────────────

  showForgotPasswordForm(): void {
    Logger.info("AdminAuthController.showForgotPasswordForm");
    const form = document.querySelector<HTMLFormElement>(FORM_SELECTOR);
    if (!form) return;

    const messages = getMessages();
    const f = new Form(form, {
      submitUrl: form.action || undefined,
      validate: (formEl) => {
        const email = (formEl.querySelector<HTMLInputElement>('[name="email"]')?.value ?? "").trim();
        return email ? null : messages.emailRequired;
      },
      loaderSelector: LOADER_SELECTOR,
      requestFailedMessage: messages.requestFailed,
      invalidResponseMessage: messages.errorOccurred,
      onSuccess: (data) => {
        const msg = (data?.msg ?? data?.message ?? "발송되었습니다.") as string;
        Toast.success(msg);
      },
      onFail: (message) => void Modal.alert(message),
    });
    f.init();
  },

  forgotPassword(): void {
    Logger.debug("AdminAuthController.forgotPassword (alias → showForgotPasswordForm)");
    AdminAuthController.showForgotPasswordForm();
  },

  showResetPasswordForm(): void {
    Logger.info("AdminAuthController.showResetPasswordForm");
    const form = document.querySelector<HTMLFormElement>(FORM_SELECTOR);
    if (!form) return;

    initPasswordToggles();
    const messages = getMessages();
    const f = new Form(form, {
      submitUrl: form.action || undefined,
      validate: (formEl) => {
        const pw  = (formEl.querySelector<HTMLInputElement>('[name="password"]')?.value ?? "").trim();
        const pwc = (formEl.querySelector<HTMLInputElement>('[name="password_confirmation"]')?.value ?? "").trim();
        if (!pw)  return messages.passwordRequired;
        if (!pwc) return messages.passwordConfirmRequired;
        if (pw !== pwc) return messages.passwordMismatch;
        return null;
      },
      loaderSelector: LOADER_SELECTOR,
      requestFailedMessage: messages.requestFailed,
      invalidResponseMessage: messages.errorOccurred,
      onSuccess: handleAuthSuccess,
      onFail: (message) => void Modal.alert(message),
    });
    f.init();
  },

  handleResetPassword(): void {
    Logger.debug("AdminAuthController.handleResetPassword (alias → showResetPasswordForm)");
    AdminAuthController.showResetPasswordForm();
  },
};
